import {
    useState,
    useEffect,
    useMemo,
    useRef,
    useImperativeHandle,
    forwardRef,
    useCallback,
} from "react";
import {
    faPlus,
    faTrash,
    faQuestionCircle,
    faCheck,
    faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Checkbox from "@mui/material/Checkbox";
import {
    Tooltip,
    Autocomplete,
    TextField as MuiTextField,
    CircularProgress,
    Link,
} from "@mui/material";

import Button from "components/Common/Button/Button";
import ButtonSmall from "components/Common/ButtonSmall/ButtonSmall";
import Select from "components/Common/Select/Select";
import TextField from "components/Common/TextField/TextField";
import BbgField from "components/Common/BbgField/BbgField";
import * as Database from "utils/database.utils";

import {
    cyclicalityOptions,
    riskBuckets,
    gicsSectorOptions,
} from "assets/data/lists";
import { useCategoryList } from "../CreateCompanyModal/useCategoryList";
import {
    useCustomCategories,
    type CustomCategory,
} from "./useCustomCategories";
import {
    useBbgEditableFields,
    type BbgEditableFields,
    type BbgFieldName,
} from "./useBbgEditableFields";
import CompanyFormContainer from "./CompanyForm.styles";
import { useSettings } from "context/Settings.context";

const extendedRiskBuckets = [
    ...riskBuckets,
    { label: "China Internet", value: "China Internet" },
    { label: "Consumer Cyclical", value: "Consumer Cyclical" },
    { label: "High-multiple Consumer", value: "High-multiple Consumer" },
    { label: "Indian Financials", value: "Indian Financials" },
    { label: "Non-Indian Financials", value: "Non-Indian Financials" },
]
    .sort((a, b) => a.label.localeCompare(b.label))
    .concat({ label: "Other", value: "Other" });

export type SelectOption = { label: string; value: string };

export type CompanyLink = {
    link_type_value: string;
    link_type_id: string | null;
    url: string;
};

export type CompanyFormData = {
    name: string;
    ticker: string;
    team_id: string;
    analyst_id: string;
    gef_focus_list: boolean;
    aef_focus_list: boolean;
    link_to_model: string;
    ipo_flag: boolean;
    cyclicality: string;
    isin: string;
    sedol: string;
    cusip: string;
    eu_composite_ticker: string;
    sector_risk: string;
    genim_gics_sector: string;
    gef_max_wgt_adj: string;
    packaged_software: boolean;
    med_tools: boolean;
    private_flag: boolean;
};

export const initialFormData: CompanyFormData = {
    name: "",
    ticker: "",
    team_id: "",
    analyst_id: "",
    gef_focus_list: false,
    aef_focus_list: false,
    link_to_model: "",
    ipo_flag: false,
    cyclicality: "",
    isin: "",
    sedol: "",
    cusip: "",
    eu_composite_ticker: "",
    sector_risk: "",
    genim_gics_sector: "",
    gef_max_wgt_adj: "",
    packaged_software: false,
    med_tools: false,
    private_flag: false,
};

type BbgStatus = "idle" | "loading" | "success" | "error";

type Props = {
    initialData?: CompanyFormData;
    initialLinks?: CompanyLink[];
    initialSectors?: Array<{ sector: string; percentage: string }>;
    initialRisks?: Array<{ risk: string; percentage: string }>;
    initialCustomCategories?: CustomCategory[];
    showBbgFields?: boolean;
    hideIpoCheckbox?: boolean;
    isEditMode?: boolean;
    bbgStatus?: BbgStatus;
    bbgEditableFields?: BbgEditableFields;
    onEnableBbgField?: (field: BbgFieldName) => void;
    onRetryBbgFetch?: () => void;
    fieldErrors?: Record<string, string>;
    setFieldErrors?: (errors: Record<string, string>) => void;
};

export type CompanyFormRef = {
    getFormData: () => CompanyFormData;
    validate: () => Record<string, string>;
    prepareSubmitData: () => Promise<any>;
    updateFormData: (data: Partial<CompanyFormData>) => void;
};

const CompanyForm = forwardRef<CompanyFormRef, Props>(
    (
        {
            initialData,
            initialLinks,
            initialSectors,
            initialRisks,
            initialCustomCategories,
            showBbgFields = false,
            hideIpoCheckbox = false,
            isEditMode = false,
            bbgStatus = "idle",
            bbgEditableFields: externalBbgEditableFields,
            onEnableBbgField,
            onRetryBbgFetch,
            fieldErrors: externalFieldErrors,
            setFieldErrors: setExternalFieldErrors,
        },
        ref
    ) => {
        const [formData, setFormData] = useState<CompanyFormData>(
            initialData || initialFormData
        );

        const internalBbg = useBbgEditableFields();
        const isControlled = externalBbgEditableFields !== undefined;
        const bbgEditableFields = isControlled
            ? externalBbgEditableFields
            : internalBbg.fields;

        useEffect(() => {
            if (!isControlled && isEditMode) {
                internalBbg.disableAll();
            }
        }, [isControlled, isEditMode, internalBbg]);

        const handleEnableBbgField = useCallback(
            (field: BbgFieldName) => {
                if (onEnableBbgField) {
                    onEnableBbgField(field);
                } else {
                    internalBbg.enableField(field);
                }
            },
            [onEnableBbgField, internalBbg]
        );

        const [analystOptions, setAnalystOptions] = useState<SelectOption[]>(
            []
        );
        const [teamOptions, setTeamOptions] = useState<SelectOption[]>([]);
        const [sectorOptions, setSectorOptions] = useState<SelectOption[]>([]);
        const [riskOptions, setRiskOptions] = useState<SelectOption[]>([]);
        const [linkTypeOptions, setLinkTypeOptions] = useState<SelectOption[]>(
            []
        );
        const [companyLinks, setCompanyLinks] = useState<CompanyLink[]>(
            initialLinks || [
                { link_type_value: "", link_type_id: null, url: "" },
            ]
        );
        const [internalFieldErrors, setInternalFieldErrors] = useState<
            Record<string, string>
        >({});

        const fieldErrors = externalFieldErrors ?? internalFieldErrors;
        const setFieldErrors = setExternalFieldErrors ?? setInternalFieldErrors;

        const [customCategoryOptions, setCustomCategoryOptions] = useState<
            SelectOption[]
        >([]);

        const [customCatItemOptions, setCustomCatItemOptions] = useState<
            Map<string, SelectOption[]>
        >(new Map());

        const { setToast } = useSettings();

        const sectors = useCategoryList("sector", sectorOptions);
        const risks = useCategoryList("risk", riskOptions);
        const customCategories = useCustomCategories();

        const fetchCategoryItems = useCallback(
            async (categoryId: string) => {
                if (customCatItemOptions.has(categoryId)) return;
                try {
                    const res = await Database.get(
                        `api/company/category-items/${categoryId}`
                    );
                    if (!res.ok) return;
                    const data = await res.json();
                    const options: SelectOption[] = data.data.map(
                        (item: { name: string; id: number }) => ({
                            label: item.name,
                            value: String(item.id),
                        })
                    );
                    setCustomCatItemOptions((prev) => {
                        const next = new Map(prev);
                        next.set(categoryId, options);
                        return next;
                    });
                } catch {
                    setToast({
                        show: true,
                        type: "error",
                        message: "Failed to load category items",
                    });
                }
            },
            [customCatItemOptions]
        );

        const handleCustomCategoryNameChange = useCallback(
            (
                catIndex: number,
                option: { label: string; value: string } | string | null
            ) => {
                customCategories.updateCategoryName(catIndex, option);
                if (option && typeof option !== "string" && option.value) {
                    fetchCategoryItems(option.value);
                }
            },
            [customCategories, fetchCategoryItems]
        );

        const getAvailableItemOptions = useCallback(
            (catIndex: number, currentItemName: string): SelectOption[] => {
                const cat = customCategories.categories[catIndex];
                if (!cat?.category_id) return [];
                const options = customCatItemOptions.get(cat.category_id) || [];
                const selectedNames = cat.items
                    .map((item) => item.item_name)
                    .filter(Boolean);
                return options.filter(
                    (opt) =>
                        opt.label === currentItemName ||
                        !selectedNames.includes(opt.label)
                );
            },
            [customCategories.categories, customCatItemOptions]
        );

        useEffect(() => {
            const parseOptions = async (
                res: Response
            ): Promise<SelectOption[]> => {
                if (!res.ok) return [];
                const data = await res.json();
                return data.data.map((item: { name: string; id: string }) => ({
                    label: item.name,
                    value: String(item.id),
                }));
            };

            const fetchFormData = async () => {
                try {
                    const responses = await Promise.all([
                        Database.get("api/company/analysts"),
                        Database.get("api/company/category-items/1"),
                        Database.get("api/company/category-items/2"),
                        Database.get("api/company/link-types"),
                        Database.get(
                            "api/company/teams?usedInCompanyManagement=true"
                        ),
                        Database.get("api/company/categories?is_custom=true"),
                    ]);

                    const [
                        analysts,
                        sectorOpts,
                        riskOpts,
                        linkTypes,
                        teams,
                        customCats,
                    ] = await Promise.all(responses.map(parseOptions));

                    setAnalystOptions(analysts);
                    setSectorOptions(sectorOpts);
                    setRiskOptions(riskOpts);
                    setLinkTypeOptions(linkTypes);
                    setTeamOptions(teams);
                    setCustomCategoryOptions(customCats);
                } catch (err) {
                    console.error("Error fetching form data:", err);
                }
            };

            fetchFormData();
        }, []);

        useEffect(() => {
            if (initialData) {
                setFormData(initialData);
            }
        }, [initialData]);

        useEffect(() => {
            if (initialLinks && initialLinks.length > 0) {
                setCompanyLinks(initialLinks);
            }
        }, [initialLinks]);

        useEffect(() => {
            if (initialSectors && initialSectors.length > 0) {
                sectors.setItems(initialSectors);
            }
        }, [initialSectors]);

        useEffect(() => {
            if (initialRisks && initialRisks.length > 0) {
                risks.setItems(initialRisks);
            }
        }, [initialRisks]);

        useEffect(() => {
            if (initialCustomCategories && initialCustomCategories.length > 0) {
                customCategories.setCategories(initialCustomCategories);
                initialCustomCategories.forEach((cat) => {
                    if (cat.category_id) {
                        fetchCategoryItems(cat.category_id);
                    }
                });
            }
        }, [initialCustomCategories]);

        const selectedLinkTypes = useMemo(
            () =>
                companyLinks
                    .map((item) => item.link_type_value)
                    .filter(Boolean),
            [companyLinks]
        );

        const getAvailableLinkTypeOptions = (currentValue: string) =>
            linkTypeOptions.filter(
                (opt) =>
                    opt.label === currentValue ||
                    !selectedLinkTypes.includes(opt.label)
            );

        const addCompanyLink = () => {
            setCompanyLinks([
                ...companyLinks,
                { link_type_value: "", link_type_id: null, url: "" },
            ]);
        };

        const removeCompanyLink = (index: number) => {
            const newLinks = companyLinks.filter((_, i) => i !== index);
            setCompanyLinks(
                newLinks.length > 0
                    ? newLinks
                    : [{ link_type_value: "", link_type_id: null, url: "" }]
            );
        };

        const updateCompanyLinkType = (
            index: number,
            option: { label: string; value: string } | string | null
        ) => {
            const newLinks = [...companyLinks];
            if (option === null) {
                newLinks[index].link_type_value = "";
                newLinks[index].link_type_id = null;
            } else if (typeof option === "string") {
                newLinks[index].link_type_value = option;
                newLinks[index].link_type_id = null;
            } else {
                newLinks[index].link_type_value = option.label;
                newLinks[index].link_type_id = option.value;
            }
            setCompanyLinks(newLinks);
        };

        const updateCompanyLinkUrl = (index: number, url: string) => {
            const newLinks = [...companyLinks];
            newLinks[index].url = url;
            setCompanyLinks(newLinks);
        };

        const updateField = <K extends keyof CompanyFormData>(
            field: K,
            value: CompanyFormData[K]
        ) => {
            setFormData((prev) => ({ ...prev, [field]: value }));
            if (fieldErrors[field]) {
                setFieldErrors({
                    ...fieldErrors,
                    [field]: undefined,
                } as Record<string, string>);
            }
        };

        const validate = (): Record<string, string> => {
            const errors: Record<string, string> = {};

            const usedLinkTypes = new Set<string>();
            companyLinks.forEach((link, index) => {
                const hasType = !!link.link_type_value;
                const hasUrl = !!link.url.trim();
                if (hasType && !hasUrl) {
                    errors[`link_url_${index}`] =
                        "URL is required when link type is selected";
                }
                if (hasUrl && !hasType) {
                    errors[`link_type_${index}`] =
                        "Link type is required when URL is entered";
                }
                if (hasType) {
                    if (usedLinkTypes.has(link.link_type_value)) {
                        errors[`link_type_${index}`] = "Duplicate link type";
                    } else {
                        usedLinkTypes.add(link.link_type_value);
                    }
                }
            });

            if (formData.gef_focus_list) {
                Object.assign(
                    errors,
                    sectors.getValidationErrors(
                        "sector_select_",
                        "sector_percentage_",
                        "economic_sectors_sum"
                    )
                );
            }

            if (formData.aef_focus_list) {
                Object.assign(
                    errors,
                    risks.getValidationErrors(
                        "risk_select_",
                        "risk_percentage_",
                        "thematic_risks_sum"
                    )
                );
            }

            Object.assign(errors, customCategories.getValidationErrors());

            return errors;
        };

        const prepareSubmitData = async () => {
            const category_items: {
                category_item_id: string;
                category_id: number;
                percentage: number;
            }[] = [];

            sectors.items.forEach((item) => {
                if (item.sector && item.percentage) {
                    category_items.push({
                        category_item_id: item.sector,
                        category_id: 1,
                        percentage: parseFloat(item.percentage),
                    });
                }
            });

            risks.items.forEach((item) => {
                if (item.risk && item.percentage) {
                    category_items.push({
                        category_item_id: item.risk,
                        category_id: 2,
                        percentage: parseFloat(item.percentage),
                    });
                }
            });

            for (const cat of customCategories.categories) {
                if (!cat.category_name) continue;
                const hasItems = cat.items.some(
                    (item) => item.item_name && item.percentage
                );
                if (!hasItems) continue;

                let categoryId = cat.category_id
                    ? parseInt(cat.category_id)
                    : null;

                if (!categoryId) {
                    const createRes = await Database.post(
                        "api/company/categories",
                        { name: cat.category_name }
                    );
                    if (createRes.ok || createRes.status === 409) {
                        const newCat = await createRes.json();
                        categoryId = newCat.id;
                        setCustomCategoryOptions((prev) => [
                            ...prev,
                            {
                                label: newCat.name,
                                value: String(newCat.id),
                            },
                        ]);
                    }
                }

                if (!categoryId) continue;

                for (const item of cat.items) {
                    if (!item.item_name || !item.percentage) continue;

                    let itemId = item.item_id ? parseInt(item.item_id) : null;

                    if (!itemId) {
                        const createRes = await Database.post(
                            "api/company/category-items",
                            {
                                name: item.item_name,
                                category_id: categoryId,
                            }
                        );
                        if (createRes.ok || createRes.status === 409) {
                            const newItem = await createRes.json();
                            itemId = newItem.id;
                        }
                    }

                    if (itemId) {
                        category_items.push({
                            category_item_id: String(itemId),
                            category_id: categoryId,
                            percentage: parseFloat(item.percentage),
                        });
                    }
                }
            }

            const company_links: { link_type_id: string; url: string }[] = [];
            for (const link of companyLinks) {
                if (link.url && link.link_type_value) {
                    let link_type_id = link.link_type_id;

                    if (!link_type_id && link.link_type_value) {
                        const createRes = await Database.post(
                            "api/company/link-types",
                            { name: link.link_type_value }
                        );
                        if (createRes.ok) {
                            const newType = await createRes.json();
                            link_type_id = newType.id;
                            setLinkTypeOptions((prev) => [
                                ...prev,
                                { label: newType.name, value: newType.id },
                            ]);
                        }
                    }

                    if (link_type_id) {
                        company_links.push({ link_type_id, url: link.url });
                    }
                }
            }

            return {
                ...formData,
                name: formData.name || null,
                ticker: formData.ticker || null,
                analyst_id: formData.analyst_id || null,
                link_to_model: formData.link_to_model || null,
                cyclicality: formData.cyclicality || null,
                team_id: formData.team_id ? parseInt(formData.team_id) : null,
                isin: formData.isin || null,
                sedol: formData.sedol || null,
                cusip: formData.cusip || null,
                eu_composite_ticker: formData.eu_composite_ticker || null,
                sector_risk: formData.sector_risk || null,
                genim_gics_sector: formData.genim_gics_sector || null,
                gef_max_wgt_adj: formData.gef_max_wgt_adj
                    ? parseFloat(formData.gef_max_wgt_adj)
                    : null,
                packaged_software: formData.packaged_software || null,
                med_tools: formData.med_tools || null,
                private_flag: formData.private_flag || null,
                category_items,
                company_links,
            };
        };

        useImperativeHandle(ref, () => ({
            getFormData: () => formData,
            validate,
            prepareSubmitData,
            updateFormData: (data: Partial<CompanyFormData>) => {
                setFormData((prev) => ({ ...prev, ...data }));
            },
        }));

        const isBbgFieldDisabled = (field: BbgFieldName) => {
            return bbgStatus === "loading" || !bbgEditableFields[field];
        };

        const containerRef = useRef<HTMLDivElement>(null);

        const handleAddCategory = () => {
            customCategories.addCategory();
            requestAnimationFrame(() => {
                let scrollable: HTMLElement | null =
                    containerRef.current?.parentElement ?? null;
                while (scrollable) {
                    const { overflowY } = window.getComputedStyle(scrollable);
                    if (overflowY === "auto" || overflowY === "scroll") break;
                    scrollable = scrollable.parentElement;
                }
                scrollable?.scrollTo({
                    top: scrollable.scrollHeight,
                    behavior: "smooth",
                });
            });
        };

        return (
            <CompanyFormContainer ref={containerRef}>
                <div className={"formGrid compact"}>
                    {isEditMode ? (
                        <BbgField
                            label="Ticker"
                            value={formData.ticker}
                            placeholder="Enter ticker symbol"
                            bbgStatus={bbgStatus}
                            isDisabled={!bbgEditableFields["ticker"]}
                            onEdit={() => handleEnableBbgField("ticker")}
                            onChange={(val) => updateField("ticker", val)}
                            error={fieldErrors.ticker}
                            showRefetch
                            onRefetch={onRetryBbgFetch}
                        />
                    ) : (
                        <div>
                            <div className="labelWithStatus">
                                <label>Ticker</label>
                                {showBbgFields && bbgStatus === "loading" && (
                                    <span className="bbgStatus loading">
                                        <CircularProgress size={12} />
                                        Fetching from Bloomberg...
                                    </span>
                                )}
                                {showBbgFields && bbgStatus === "success" && (
                                    <span className="bbgStatus success">
                                        <FontAwesomeIcon icon={faCheck} />
                                        Fetched from Bloomberg
                                    </span>
                                )}
                                {showBbgFields && bbgStatus === "error" && (
                                    <>
                                        <span className="bbgStatus error">
                                            <FontAwesomeIcon
                                                icon={faExclamationTriangle}
                                            />
                                            Failed to fetch from Bloomberg
                                        </span>
                                        <Link
                                            component="button"
                                            variant="body2"
                                            onClick={onRetryBbgFetch}
                                            underline="hover"
                                            sx={{ fontSize: 11, ml: 1 }}
                                        >
                                            Retry
                                        </Link>
                                    </>
                                )}
                            </div>
                            <TextField
                                value={formData.ticker}
                                change={(val: string) =>
                                    updateField("ticker", val)
                                }
                                placeholder={"Enter ticker symbol"}
                                disabled={
                                    showBbgFields && bbgStatus !== "error"
                                }
                            />
                        </div>
                    )}

                    {showBbgFields ? (
                        <>
                            <BbgField
                                label="Company Name"
                                value={formData.name}
                                placeholder="Company name"
                                bbgStatus={bbgStatus}
                                isDisabled={isBbgFieldDisabled("name")}
                                onEdit={() => handleEnableBbgField("name")}
                                onChange={(val) => updateField("name", val)}
                                error={fieldErrors.name}
                            />
                            <BbgField
                                label="ISIN"
                                value={formData.isin}
                                placeholder="ISIN"
                                bbgStatus={bbgStatus}
                                isDisabled={isBbgFieldDisabled("isin")}
                                onEdit={() => handleEnableBbgField("isin")}
                                onChange={(val) => updateField("isin", val)}
                                error={fieldErrors.isin}
                            />
                            <BbgField
                                label="SEDOL ID"
                                value={formData.sedol}
                                placeholder="SEDOL ID"
                                bbgStatus={bbgStatus}
                                isDisabled={isBbgFieldDisabled("sedol")}
                                onEdit={() => handleEnableBbgField("sedol")}
                                onChange={(val) => updateField("sedol", val)}
                                error={fieldErrors.sedol}
                            />
                            <BbgField
                                label="CUSIP ID"
                                value={formData.cusip}
                                placeholder="CUSIP ID"
                                bbgStatus={bbgStatus}
                                isDisabled={isBbgFieldDisabled("cusip")}
                                onEdit={() => handleEnableBbgField("cusip")}
                                onChange={(val) => updateField("cusip", val)}
                                error={fieldErrors.cusip}
                            />
                            <BbgField
                                label="EU Composite Ticker"
                                value={formData.eu_composite_ticker}
                                placeholder="EU Composite Ticker"
                                bbgStatus={bbgStatus}
                                isDisabled={isBbgFieldDisabled(
                                    "eu_composite_ticker"
                                )}
                                onEdit={() =>
                                    handleEnableBbgField("eu_composite_ticker")
                                }
                                onChange={(val) =>
                                    updateField("eu_composite_ticker", val)
                                }
                                error={fieldErrors.eu_composite_ticker}
                            />
                        </>
                    ) : (
                        <>
                            <div>
                                <label>Company Name</label>
                                <TextField
                                    value={formData.name}
                                    change={(val: string) =>
                                        updateField("name", val)
                                    }
                                    placeholder={"Company name"}
                                    error={!!fieldErrors.name}
                                    errorText={fieldErrors.name}
                                />
                            </div>
                            <div>
                                <label>ISIN</label>
                                <TextField
                                    value={formData.isin}
                                    change={(val: string) =>
                                        updateField("isin", val)
                                    }
                                    placeholder={"ISIN"}
                                    error={!!fieldErrors.isin}
                                    errorText={fieldErrors.isin}
                                />
                            </div>
                            <div>
                                <label>SEDOL ID</label>
                                <TextField
                                    value={formData.sedol}
                                    change={(val: string) =>
                                        updateField("sedol", val)
                                    }
                                    placeholder={"SEDOL ID"}
                                    error={!!fieldErrors.sedol}
                                    errorText={fieldErrors.sedol}
                                />
                            </div>
                            <div>
                                <label>CUSIP ID</label>
                                <TextField
                                    value={formData.cusip}
                                    change={(val: string) =>
                                        updateField("cusip", val)
                                    }
                                    placeholder={"CUSIP ID"}
                                    error={!!fieldErrors.cusip}
                                    errorText={fieldErrors.cusip}
                                />
                            </div>
                            <div>
                                <label>EU Composite Ticker</label>
                                <TextField
                                    value={formData.eu_composite_ticker}
                                    change={(val: string) =>
                                        updateField("eu_composite_ticker", val)
                                    }
                                    placeholder={"EU Composite Ticker"}
                                    error={!!fieldErrors.eu_composite_ticker}
                                    errorText={fieldErrors.eu_composite_ticker}
                                />
                            </div>
                        </>
                    )}

                    <div>
                        <label>Economic Sector/Thematic Risk</label>
                        <Select
                            filterName={"Select"}
                            options={extendedRiskBuckets}
                            value={formData.sector_risk}
                            setValue={(val: string) =>
                                updateField("sector_risk", val)
                            }
                            showAll={false}
                            error={!!fieldErrors.sector_risk}
                            errorText={fieldErrors.sector_risk}
                        />
                    </div>
                    <div style={{ marginBottom: "20px" }}>
                        <label>Team</label>
                        <Select
                            filterName={"Select"}
                            options={teamOptions}
                            value={formData.team_id}
                            setValue={(val: string) =>
                                updateField("team_id", val)
                            }
                            showAll={false}
                            error={!!fieldErrors.team_id}
                            errorText={fieldErrors.team_id}
                        />
                    </div>
                    <div>
                        <label>Analyst</label>
                        <Select
                            filterName={"Select"}
                            options={analystOptions}
                            value={formData.analyst_id}
                            setValue={(val: string) =>
                                updateField("analyst_id", val)
                            }
                            showAll={false}
                            error={!!fieldErrors.analyst_id}
                            errorText={fieldErrors.analyst_id}
                            searchable
                        />
                    </div>
                    <div className="checkboxGrid">
                        <div className={"checkboxField"}>
                            <Checkbox
                                checked={formData.gef_focus_list}
                                onChange={(e) => {
                                    updateField(
                                        "gef_focus_list",
                                        e.target.checked
                                    );
                                    if (!e.target.checked) {
                                        sectors.reset();
                                    }
                                }}
                                disableRipple
                            />
                            <label>GEF FL</label>
                        </div>
                        <div className={"checkboxField"}>
                            <Checkbox
                                checked={formData.aef_focus_list}
                                onChange={(e) => {
                                    updateField(
                                        "aef_focus_list",
                                        e.target.checked
                                    );
                                    if (!e.target.checked) {
                                        risks.reset();
                                    }
                                }}
                                disableRipple
                            />
                            <label>AEF FL</label>
                        </div>
                        <div className={"checkboxField"}>
                            {!hideIpoCheckbox && (
                                <>
                                    <Checkbox
                                        checked={formData.ipo_flag}
                                        onChange={(e) =>
                                            updateField(
                                                "ipo_flag",
                                                e.target.checked
                                            )
                                        }
                                        disableRipple
                                    />
                                    <label>IPO</label>
                                </>
                            )}
                        </div>
                        <div className={"checkboxField"}>
                            <Checkbox
                                checked={formData.packaged_software}
                                onChange={(e) =>
                                    updateField(
                                        "packaged_software",
                                        e.target.checked
                                    )
                                }
                                disableRipple
                            />
                            <label>Packaged Software</label>
                        </div>
                        <div className={"checkboxField"}>
                            <Checkbox
                                checked={formData.med_tools}
                                onChange={(e) =>
                                    updateField("med_tools", e.target.checked)
                                }
                                disableRipple
                            />
                            <label>Med Tools</label>
                        </div>
                        <div className={"checkboxField"}>
                            <Checkbox
                                checked={formData.private_flag}
                                onChange={(e) =>
                                    updateField(
                                        "private_flag",
                                        e.target.checked
                                    )
                                }
                                disableRipple
                            />
                            <label>Private</label>
                        </div>
                    </div>
                    <div className="twoColumns">
                        <label>
                            P Drive Link to Company Model{" "}
                            <Tooltip
                                placement="top"
                                title='Right-click on the Excel file and select "Copy as path", or select the file and press Ctrl+Shift+C'
                            >
                                <FontAwesomeIcon
                                    icon={faQuestionCircle}
                                    size="lg"
                                />
                            </Tooltip>
                        </label>
                        <TextField
                            value={formData.link_to_model}
                            change={(val: string) =>
                                updateField("link_to_model", val)
                            }
                            placeholder={
                                "e.g., P:/Global Technology/02-Stocks/Apple"
                            }
                            error={!!fieldErrors.link_to_model}
                            errorText={fieldErrors.link_to_model}
                        />
                    </div>
                    <div>
                        <label>
                            GEF Max Weight Adjustment (%){" "}
                            <Tooltip
                                placement="top"
                                title="This field is optional."
                            >
                                <FontAwesomeIcon
                                    icon={faQuestionCircle}
                                    size="lg"
                                />
                            </Tooltip>
                        </label>
                        <TextField
                            value={formData.gef_max_wgt_adj}
                            change={(val: string) =>
                                updateField("gef_max_wgt_adj", val)
                            }
                            placeholder={"Enter percentage"}
                            type="number"
                            step="0.1"
                            error={!!fieldErrors.gef_max_wgt_adj}
                            errorText={fieldErrors.gef_max_wgt_adj}
                        />
                    </div>
                    <div>
                        <label>
                            GenIM GICS Sector Override{" "}
                            <Tooltip
                                placement="top"
                                title="This field is optional."
                            >
                                <FontAwesomeIcon
                                    icon={faQuestionCircle}
                                    size="lg"
                                />
                            </Tooltip>
                        </label>
                        <Select
                            filterName={"Select"}
                            options={gicsSectorOptions}
                            value={formData.genim_gics_sector}
                            setValue={(val: string) =>
                                updateField("genim_gics_sector", val)
                            }
                            showAll={false}
                            error={!!fieldErrors.genim_gics_sector}
                            errorText={fieldErrors.genim_gics_sector}
                            clearable
                        />
                    </div>
                    <div>
                        <label>Cyclicality</label>
                        <Select
                            filterName={"Select"}
                            options={cyclicalityOptions}
                            value={formData.cyclicality}
                            setValue={(val: string) =>
                                updateField("cyclicality", val)
                            }
                            showAll={false}
                            error={!!fieldErrors.cyclicality}
                            errorText={fieldErrors.cyclicality}
                            clearable
                        />
                    </div>
                </div>

                <div className={"dynamicFieldsContainer"}>
                    <div className={"dynamicFieldSection"}>
                        <div className={"sectionHeader"}>
                            <label>
                                External Links{" "}
                                <Tooltip
                                    placement="top"
                                    title="The hyperlink should redirect to a particular company in the external platform/system."
                                >
                                    <FontAwesomeIcon
                                        icon={faQuestionCircle}
                                        size="lg"
                                    />
                                </Tooltip>
                            </label>
                            <ButtonSmall
                                text={"ADD"}
                                color={"primary"}
                                icon={faPlus}
                                click={addCompanyLink}
                            />
                        </div>
                        {companyLinks.map((item, index) => (
                            <div
                                key={index}
                                className={"dynamicFieldRow linkRow"}
                            >
                                <div className={"linkTypeField"}>
                                    <Autocomplete
                                        freeSolo
                                        options={getAvailableLinkTypeOptions(
                                            item.link_type_value
                                        )}
                                        getOptionLabel={(option) =>
                                            typeof option === "string"
                                                ? option
                                                : option.label
                                        }
                                        value={
                                            item.link_type_id
                                                ? linkTypeOptions.find(
                                                      (opt) =>
                                                          opt.value ===
                                                          item.link_type_id
                                                  ) || null
                                                : item.link_type_value || null
                                        }
                                        onChange={(_, newValue) =>
                                            updateCompanyLinkType(
                                                index,
                                                newValue
                                            )
                                        }
                                        onInputChange={(
                                            _,
                                            newInputValue,
                                            reason
                                        ) => {
                                            if (reason === "input") {
                                                updateCompanyLinkType(
                                                    index,
                                                    newInputValue
                                                );
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <MuiTextField
                                                {...params}
                                                placeholder="Type or select..."
                                                size="small"
                                                error={
                                                    !!fieldErrors[
                                                        `link_type_${index}`
                                                    ]
                                                }
                                                helperText={
                                                    fieldErrors[
                                                        `link_type_${index}`
                                                    ] ||
                                                    (item.link_type_value &&
                                                    !item.link_type_id
                                                        ? "New type will be created"
                                                        : "")
                                                }
                                            />
                                        )}
                                        size="small"
                                    />
                                </div>
                                <div className={"urlField"}>
                                    <TextField
                                        value={item.url}
                                        change={(value: string) =>
                                            updateCompanyLinkUrl(index, value)
                                        }
                                        placeholder={"Enter URL"}
                                        error={
                                            !!fieldErrors[`link_url_${index}`]
                                        }
                                        errorText={
                                            fieldErrors[`link_url_${index}`]
                                        }
                                    />
                                    <div
                                        className={`deleteBtn deleteAction ${
                                            companyLinks.length <= 1
                                                ? "hidden"
                                                : ""
                                        }`}
                                    >
                                        <ButtonSmall
                                            text={"Remove link"}
                                            color={"third"}
                                            icon={faTrash}
                                            click={() =>
                                                removeCompanyLink(index)
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {formData.gef_focus_list && (
                        <div className={"dynamicFieldSection"}>
                            <div className={"sectionHeader"}>
                                <label>
                                    Economic Sectors{" "}
                                    <Tooltip
                                        placement="top"
                                        title="The total percentage across all sectors should sum to 100%."
                                    >
                                        <FontAwesomeIcon
                                            icon={faQuestionCircle}
                                            size="lg"
                                        />
                                    </Tooltip>{" "}
                                    {sectors.sumError && (
                                        <span className="sectionError">
                                            {sectors.sumError}
                                        </span>
                                    )}
                                </label>
                                <ButtonSmall
                                    text={"ADD"}
                                    color={"primary"}
                                    icon={faPlus}
                                    click={sectors.add}
                                    disabled={!sectors.canAddMore}
                                />
                            </div>
                            {sectors.items.map((item, index) => (
                                <div key={index} className={"dynamicFieldRow"}>
                                    <div className={"selectField"}>
                                        <Select
                                            filterName={"Select Sector"}
                                            options={sectors.getAvailableOptions(
                                                item.sector
                                            )}
                                            value={item.sector}
                                            setValue={(value: string) =>
                                                sectors.update(
                                                    index,
                                                    "sector",
                                                    value
                                                )
                                            }
                                            showAll={false}
                                            error={
                                                !!fieldErrors[
                                                    `sector_select_${index}`
                                                ]
                                            }
                                        />
                                    </div>
                                    <div className={"percentageField"}>
                                        <TextField
                                            value={item.percentage}
                                            change={(value: string) =>
                                                sectors.update(
                                                    index,
                                                    "percentage",
                                                    value
                                                )
                                            }
                                            placeholder={"%"}
                                            type="number"
                                            displayIcon={false}
                                            error={
                                                !!fieldErrors[
                                                    `sector_percentage_${index}`
                                                ]
                                            }
                                        />
                                    </div>
                                    <div
                                        className={`deleteAction ${
                                            sectors.items.length <= 1
                                                ? "hidden"
                                                : ""
                                        }`}
                                    >
                                        <ButtonSmall
                                            text={"Remove sector"}
                                            color={"third"}
                                            icon={faTrash}
                                            click={() => sectors.remove(index)}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {formData.aef_focus_list && (
                        <div className={"dynamicFieldSection"}>
                            <div className={"sectionHeader"}>
                                <label>
                                    Thematic Risks{" "}
                                    <Tooltip
                                        placement="top"
                                        title="The total percentage across all risks should sum to 100%."
                                    >
                                        <FontAwesomeIcon
                                            icon={faQuestionCircle}
                                            size="lg"
                                        />
                                    </Tooltip>{" "}
                                    {risks.sumError && (
                                        <span className="sectionError">
                                            {risks.sumError}
                                        </span>
                                    )}
                                </label>
                                <ButtonSmall
                                    text={"ADD"}
                                    color={"primary"}
                                    icon={faPlus}
                                    click={risks.add}
                                    disabled={!risks.canAddMore}
                                />
                            </div>
                            {risks.items.map((item, index) => (
                                <div key={index} className={"dynamicFieldRow"}>
                                    <div className={"selectField"}>
                                        <Select
                                            filterName={"Select Risk"}
                                            options={risks.getAvailableOptions(
                                                item.risk
                                            )}
                                            value={item.risk}
                                            setValue={(value: string) =>
                                                risks.update(
                                                    index,
                                                    "risk",
                                                    value
                                                )
                                            }
                                            showAll={false}
                                            error={
                                                !!fieldErrors[
                                                    `risk_select_${index}`
                                                ]
                                            }
                                        />
                                    </div>
                                    <div className={"percentageField"}>
                                        <TextField
                                            value={item.percentage}
                                            change={(value: string) =>
                                                risks.update(
                                                    index,
                                                    "percentage",
                                                    value
                                                )
                                            }
                                            placeholder={"%"}
                                            type="number"
                                            displayIcon={false}
                                            error={
                                                !!fieldErrors[
                                                    `risk_percentage_${index}`
                                                ]
                                            }
                                        />
                                    </div>
                                    <div
                                        className={`deleteAction ${
                                            risks.items.length <= 1
                                                ? "hidden"
                                                : ""
                                        }`}
                                    >
                                        <ButtonSmall
                                            text={"Remove risk"}
                                            color={"third"}
                                            icon={faTrash}
                                            click={() => risks.remove(index)}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className={"sectionHeader"}>
                        <label>Custom Categories</label>
                        <Button
                            text={"Add a custom category"}
                            color={"primary"}
                            icon={faPlus}
                            size={"medium"}
                            click={handleAddCategory}
                        />
                    </div>

                    {customCategories.categories.map((cat, catIndex) => (
                        <div
                            key={catIndex}
                            className={
                                "dynamicFieldSection customCategorySection"
                            }
                        >
                            <div
                                className={
                                    "dynamicFieldRow customCategoryRow customCategoryNameRow"
                                }
                            >
                                <div className={"customItemField"}>
                                    <Autocomplete
                                        freeSolo
                                        options={customCategoryOptions.filter(
                                            (opt) =>
                                                opt.label ===
                                                    cat.category_name ||
                                                !customCategories.categories.some(
                                                    (c, i) =>
                                                        i !== catIndex &&
                                                        c.category_name ===
                                                            opt.label
                                                )
                                        )}
                                        getOptionLabel={(option) =>
                                            typeof option === "string"
                                                ? option
                                                : option.label
                                        }
                                        value={
                                            cat.category_id
                                                ? customCategoryOptions.find(
                                                      (opt) =>
                                                          opt.value ===
                                                          cat.category_id
                                                  ) || null
                                                : cat.category_name || null
                                        }
                                        onChange={(_, newValue) =>
                                            handleCustomCategoryNameChange(
                                                catIndex,
                                                newValue
                                            )
                                        }
                                        onInputChange={(
                                            _,
                                            newInputValue,
                                            reason
                                        ) => {
                                            if (reason === "input") {
                                                handleCustomCategoryNameChange(
                                                    catIndex,
                                                    newInputValue
                                                );
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <MuiTextField
                                                {...params}
                                                placeholder="Category name..."
                                                size="small"
                                                error={
                                                    !!fieldErrors[
                                                        `custom_cat_name_${catIndex}`
                                                    ]
                                                }
                                                helperText={
                                                    fieldErrors[
                                                        `custom_cat_name_${catIndex}`
                                                    ] ||
                                                    (cat.category_name &&
                                                    !cat.category_id
                                                        ? "New category will be created"
                                                        : "")
                                                }
                                            />
                                        )}
                                        size="small"
                                    />
                                </div>
                                <div className={"categoryNameMeta"}>
                                    <label>
                                        <Tooltip
                                            placement="top"
                                            title="The total percentage across all items should sum to 100%."
                                        >
                                            <FontAwesomeIcon
                                                icon={faQuestionCircle}
                                                size="lg"
                                            />
                                        </Tooltip>
                                    </label>
                                    {customCategories.getSumError(catIndex) && (
                                        <span className="sectionError">
                                            {customCategories.getSumError(
                                                catIndex
                                            )}
                                        </span>
                                    )}
                                </div>
                                <div className={"deleteAction"}>
                                    <ButtonSmall
                                        text={"Remove this category"}
                                        color={"third"}
                                        icon={faTrash}
                                        click={() =>
                                            customCategories.removeCategory(
                                                catIndex
                                            )
                                        }
                                    />
                                </div>
                            </div>
                            {cat.category_name &&
                                cat.items.map((item, itemIndex) => (
                                    <div
                                        key={itemIndex}
                                        className={
                                            "dynamicFieldRow customCategoryRow"
                                        }
                                    >
                                        <div className={"customItemField"}>
                                            <Autocomplete
                                                freeSolo
                                                options={getAvailableItemOptions(
                                                    catIndex,
                                                    item.item_name
                                                )}
                                                getOptionLabel={(option) =>
                                                    typeof option === "string"
                                                        ? option
                                                        : option.label
                                                }
                                                value={
                                                    item.item_id
                                                        ? getAvailableItemOptions(
                                                              catIndex,
                                                              item.item_name
                                                          ).find(
                                                              (opt) =>
                                                                  opt.value ===
                                                                  item.item_id
                                                          ) || null
                                                        : item.item_name || null
                                                }
                                                onChange={(_, newValue) =>
                                                    customCategories.updateItemName(
                                                        catIndex,
                                                        itemIndex,
                                                        newValue
                                                    )
                                                }
                                                onInputChange={(
                                                    _,
                                                    newInputValue,
                                                    reason
                                                ) => {
                                                    if (reason === "input") {
                                                        customCategories.updateItemName(
                                                            catIndex,
                                                            itemIndex,
                                                            newInputValue
                                                        );
                                                    }
                                                }}
                                                renderInput={(params) => (
                                                    <MuiTextField
                                                        {...params}
                                                        placeholder="Type or select item..."
                                                        size="small"
                                                        error={
                                                            !!fieldErrors[
                                                                `custom_cat_${catIndex}_item_name_${itemIndex}`
                                                            ]
                                                        }
                                                        helperText={
                                                            fieldErrors[
                                                                `custom_cat_${catIndex}_item_name_${itemIndex}`
                                                            ] ||
                                                            (item.item_name &&
                                                            !item.item_id
                                                                ? "New item will be created"
                                                                : "")
                                                        }
                                                    />
                                                )}
                                                size="small"
                                            />
                                        </div>
                                        <div className={"percentageField"}>
                                            <TextField
                                                value={item.percentage}
                                                change={(value: string) =>
                                                    customCategories.updateItemPercentage(
                                                        catIndex,
                                                        itemIndex,
                                                        value
                                                    )
                                                }
                                                placeholder={"%"}
                                                type="number"
                                                displayIcon={false}
                                                error={
                                                    !!fieldErrors[
                                                        `custom_cat_${catIndex}_item_percentage_${itemIndex}`
                                                    ]
                                                }
                                            />
                                        </div>
                                        <div
                                            className={`deleteAction ${
                                                cat.items.length <= 1
                                                    ? "hidden"
                                                    : ""
                                            }`}
                                        >
                                            <ButtonSmall
                                                text={"Remove item"}
                                                color={"third"}
                                                icon={faTrash}
                                                click={() =>
                                                    customCategories.removeItem(
                                                        catIndex,
                                                        itemIndex
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                ))}
                            <div className={"categoryAddItemRow"}>
                                <Button
                                    text={"Add item"}
                                    color={"primary"}
                                    icon={faPlus}
                                    size={"medium"}
                                    disabled={!cat.category_name}
                                    click={() =>
                                        customCategories.addItem(catIndex)
                                    }
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </CompanyFormContainer>
        );
    }
);

export default CompanyForm;
